import { Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Building2, Briefcase, BarChart3, UserCheck, LogOut, Menu, X, FileText, Mail, UserCog, ClipboardList, Star, PlusCircle, Database, MapPin, Settings, MessageCircle, Award, Image, Send, CalendarDays, CalendarPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getUser, clearAuth } from '@/utils/auth';
import { toast } from 'sonner';
import AdminDashboardHome from './AdminDashboardHome';
import EmployerManagement from './EmployerManagement';
import JobSeekerManagement from './JobSeekerManagement';
import JobManagement from './JobManagement';
import ApplicationsMonitor from './ApplicationsMonitor';
import CMSManagement from './CMSManagement';
import CMSPageEditor from './CMSPageEditor';
import AchievementsCMS from './AchievementsCMS';
import SiteSettingsCMS from './SiteSettingsCMS';
import BroadcastSettings from './BroadcastSettings';
import EnquiriesManagement from './EnquiriesManagement';
import ExecutiveManagement from './ExecutiveManagement';
import ExecutiveDashboardHome from './ExecutiveDashboardHome';
import ExecutiveJobManagement from './ExecutiveJobManagement';
import ExecutiveEmployerManagement from './ExecutiveEmployerManagement';
import ExecutiveJobSeekerManagement from './ExecutiveJobSeekerManagement';
import ExecutiveApplications from './ExecutiveApplications';
import ExecutiveActivityLogs from './ExecutiveActivityLogs';
import PrimeEmployerManagement from './PrimeEmployerManagement';
import AdminJobPosting from './AdminJobPosting';
import PrimeEmployerJobs from './PrimeEmployerJobs';
import BackupManagement from './BackupManagement';
import CityManagement from './CityManagement';
import MailSettings from './MailSettings';
import WhatsAppSettings from './WhatsAppSettings';
import LocationIntegrity from './LocationIntegrity';
import JobFairList from './jobfairs/JobFairList';
import JobFairEditor from './jobfairs/JobFairEditor';
import JobFairRegistrations from './jobfairs/JobFairRegistrations';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  const isExecutive = user?.role === 'executive';

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'executive')) {
      toast.error('Unauthorized access');
      navigate('/adminvvsi/login');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/adminvvsi/login');
  };

  // Full admin menu items
  const adminMenuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: UserCog, label: 'Executives', path: '/admin/executives' },
    { icon: Building2, label: 'Employers', path: '/admin/employers' },
    { icon: UserCheck, label: 'Job Seekers', path: '/admin/job-seekers' },
    { icon: Briefcase, label: 'Job Posts', path: '/admin/jobs' },
    { icon: Star, label: 'Prime Employers', path: '/admin/prime-employers' },
    { icon: PlusCircle, label: 'Post Job', path: '/admin/post-job' },
    { icon: ClipboardList, label: 'Applications', path: '/admin/applications' },
    { icon: MapPin, label: 'City Master', path: '/admin/cities' },
    { icon: FileText, label: 'CMS Management', path: '/admin/cms' },
    { icon: Award, label: 'Achievements', path: '/admin/achievements-cms' },
    { icon: Image, label: 'Site Settings', path: '/admin/site-settings' },
    { icon: CalendarPlus, label: 'New Job Fair', path: '/admin/jobfairs/new' },
    { icon: CalendarDays, label: 'Manage Job Fairs', path: '/admin/jobfairs' },
    { icon: Send, label: 'Broadcast', path: '/admin/broadcast-settings' },
    { icon: Mail, label: 'Enquiries', path: '/admin/enquiries' },
    { icon: Settings, label: 'Mail Settings', path: '/admin/mail-settings' },
    { icon: MessageCircle, label: 'WhatsApp', path: '/admin/whatsapp-settings' },
    { icon: Database, label: 'Backup', path: '/admin/backup' },
  ];

  // Restricted menu for executives - also can post jobs for Prime Employers
  const executiveMenuItems = [
    { icon: BarChart3, label: 'Dashboard', path: '/admin/executive-dashboard' },
    { icon: Briefcase, label: 'Job Management', path: '/admin/executive-jobs' },
    { icon: Star, label: 'Prime Employers', path: '/admin/prime-employers' },
    { icon: PlusCircle, label: 'Post Job', path: '/admin/post-job' },
    { icon: Building2, label: 'Employers', path: '/admin/executive-employers' },
    { icon: UserCheck, label: 'Job Seekers', path: '/admin/executive-job-seekers' },
  ];

  const menuItems = isExecutive ? executiveMenuItems : adminMenuItems;

  if (!user) return null;

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-slate-900 text-white transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <div className="flex items-center gap-3">
                <img src="/vvvsi-dashboard-logo.png" alt="VVVSI Logo" className="h-12 w-auto object-contain" />
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-white hover:bg-slate-800"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-teal-600 text-white' 
                    : 'hover:bg-slate-800'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
          
          {/* Activity Logs for admins viewing executive actions */}
          {!isExecutive && (
            <Link
              to="/admin/executive-activity-logs"
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                location.pathname === '/admin/executive-activity-logs'
                  ? 'bg-teal-600 text-white' 
                  : 'hover:bg-slate-800'
              }`}
            >
              <ClipboardList className="w-5 h-5" />
              {sidebarOpen && <span>Executive Logs</span>}
            </Link>
          )}
        </nav>

        <div className="p-4 border-t border-slate-700">
          {sidebarOpen ? (
            <div className="mb-3">
              <p className="text-sm text-slate-400">Logged in as</p>
              <p className="font-medium text-white truncate">{user.name || user.mobile}</p>
              <p className="text-xs text-slate-400 capitalize">
                {isExecutive ? (
                  <span className="inline-flex items-center gap-1">
                    <UserCog className="w-3 h-3" /> Executive
                  </span>
                ) : 'Admin'}
              </p>
            </div>
          ) : (
            <div className="flex justify-center mb-2">
              <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white text-sm font-medium">
                {(user.name || user.mobile || 'A').charAt(0).toUpperCase()}
              </div>
            </div>
          )}
          <Button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className={`w-full text-white hover:bg-slate-800 hover:text-white ${sidebarOpen ? 'justify-start' : 'justify-center px-0'}`}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <Routes>
            {/* Admin Routes */}
            <Route path="/" element={<Navigate to={isExecutive ? "/admin/executive-dashboard" : "/admin/dashboard"} replace />} />
            <Route path="/dashboard" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <AdminDashboardHome />} />
            <Route path="/executives" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <ExecutiveManagement />} />
            <Route path="/employers" element={isExecutive ? <Navigate to="/admin/executive-employers" replace /> : <EmployerManagement />} />
            <Route path="/job-seekers" element={isExecutive ? <Navigate to="/admin/executive-job-seekers" replace /> : <JobSeekerManagement />} />
            <Route path="/jobs" element={isExecutive ? <Navigate to="/admin/executive-jobs" replace /> : <JobManagement />} />
            <Route path="/applications" element={isExecutive ? <Navigate to="/admin/executive-applications" replace /> : <ApplicationsMonitor />} />
            <Route path="/cms" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <CMSManagement />} />
            <Route path="/cms/edit/:pageId" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <CMSPageEditor />} />
            <Route path="/achievements-cms" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <AchievementsCMS />} />
            <Route path="/site-settings" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <SiteSettingsCMS />} />
            <Route path="/broadcast-settings" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <BroadcastSettings />} />
            <Route path="/enquiries" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <EnquiriesManagement />} />
            <Route path="/executive-activity-logs" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <ExecutiveActivityLogs />} />
            
            {/* Prime Employer Routes - Available for both Admin and Executive */}
            <Route path="/prime-employers" element={<PrimeEmployerManagement />} />
            <Route path="/post-job" element={<AdminJobPosting />} />
            <Route path="/prime-employer-jobs" element={<PrimeEmployerJobs />} />
            <Route path="/backup" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <BackupManagement />} />
            <Route path="/cities" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <CityManagement />} />
            <Route path="/location-integrity" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <LocationIntegrity />} />
            <Route path="/mail-settings" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <MailSettings />} />
            <Route path="/jobfairs" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <JobFairList />} />
            <Route path="/jobfairs/new" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <JobFairList openNew />} />
            <Route path="/jobfairs/:id/edit" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <JobFairEditor />} />
            <Route path="/jobfairs/:id/registrations" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <JobFairRegistrations />} />
            <Route path="/whatsapp-settings" element={isExecutive ? <Navigate to="/admin/executive-dashboard" replace /> : <WhatsAppSettings />} />
            
            {/* Executive Routes */}
            <Route path="/executive-dashboard" element={<ExecutiveDashboardHome />} />
            <Route path="/executive-jobs" element={<ExecutiveJobManagement />} />
            <Route path="/executive-employers" element={<EmployerManagement mode="executive" executiveId={user?.id} />} />
            <Route path="/executive-job-seekers" element={<ExecutiveJobSeekerManagement />} />
            <Route path="/executive-applications" element={<ExecutiveApplications />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
